import { addToast } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import {
  addMinutes,
  durationBetween,
  findDailyLimitBreach,
  isCustomTimeOffType,
  minuteOfDay,
  sortPickableTimeOffTypes,
  type TimeOffScope,
} from "@/pages/Dashboard/Calendar/components/hooks/timeOffForm";
import type {
  GetTimeOffTypes,
  TimeOffGranularity,
  TimeOffOverlapConflictBody,
} from "@/server-types";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { getApiErrorCode } from "@/utils/api-error";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";
import { presetPercents } from "../../TimeOffTypes/timeOffCompensation";
import { resolveTimeOffTypeName } from "../../TimeOffTypes/timeOffTypeDisplayName";
import { refreshAfterTimeOffWrite } from "./refreshAfterTimeOffWrite";
import {
  blankTimeOffValues,
  canBecomeHourLevel,
  compensationField,
  findFieldErrors,
  initialWeekdayMask,
  isTimeOffPreview,
  pinRuleChangeDate,
  sameRecurrence,
  type RepeatOption,
  type TimeOffFormValues,
  type TimeOffRecurrence,
  toAbsenceBody,
  toRecurrence,
  toTimeOffBody,
  valuesFromDetail,
} from "./timeOffFormValues";
import { fieldErrorMessage, timeOffErrorMessage } from "./timeOffFormat";

export type TimeOffTypeRow = GetTimeOffTypes["response"][number];

export type TimeOffPerson = {
  userId: number;
  name: string | null;
  lastName: string | null;
};

export type TimeOffResource = { id: number; label: string | null };

export type TimeOffFormInitial = {
  date: string;
  startTime?: string;
  userId?: number | null;
  resourceId?: number | null;
  /** Which group's first tile is picked when nothing else decides it. */
  granularity?: TimeOffGranularity;
};

type PendingScope = { intent: "save" | "delete" };

type SaveError = { code: string | null; status: number | null };

/** What the opened occurrence held, to tell the user's changes apart from it. */
type EditBaseline = {
  recurrence: TimeOffRecurrence;
  typeId: number | null;
  allowsOnlineBooking: boolean;
  /** The opened override as the server would receive it, to send it only when changed. */
  compensation: string;
};

/** The calendar's "Odsotnost" tab and the absences tab; every number it shows comes from the server's dry run. */
export function useTimeOffForm({
  locationId,
  people,
  initial,
  editing,
  allowDayTypes,
  allowTimeTypes = true,
}: {
  locationId: number | null;
  people: TimeOffPerson[];
  initial: TimeOffFormInitial;
  editing: { target: "staff" | "resource"; id: number } | null;
  /** Day-level absences need `view_availability`, like the endpoint. */
  allowDayTypes: boolean;
  /** False on the absences tab, which lists day-level absences only. */
  allowTimeTypes?: boolean;
}) {
  const queryClient = useQueryClient();
  const [pendingScope, setPendingScope] = useState<PendingScope | null>(null);
  const [overlapConflict, setOverlapConflict] =
    useState<TimeOffOverlapConflictBody | null>(null);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<EditBaseline>({
    recurrence: null,
    typeId: null,
    allowsOnlineBooking: false,
    compensation: "",
  });
  const canSeeStaffCost = !!useUserPermissionsStore(
    (state) => state.permissions.view_staff_cost,
  );

  const form = useForm<TimeOffFormValues>({
    initialValues: blankTimeOffValues(initial),
  });
  const { values } = form;

  const { data: allTypes } = api.timeOffType.useGetTimeOffTypes({
    includeCompensation: canSeeStaffCost,
  });
  const { data: detail, isLoading: isLoadingDetail } =
    api.timeoff.useGetTimeOffOccurrence(editing);

  const types = allTypes ?? [];
  const selectedType = types.find((type) => type.id === values.typeId) ?? null;
  const editingGranularity =
    editing != null && detail != null
      ? (detail.type?.granularity ?? "TIME")
      : null;
  const granularity: TimeOffGranularity =
    selectedType?.granularity ??
    editingGranularity ??
    initial.granularity ??
    "TIME";
  const isDay = granularity === "DAY";
  const canLeaveAbsence =
    editing?.target === "staff" && detail != null && canBecomeHourLevel(detail);

  // An archived type stays offered only on the occurrence already using it.
  const offered = types.filter(
    (type) =>
      (!type.isArchived || type.id === values.typeId) &&
      (allowDayTypes || type.granularity === "TIME") &&
      (allowTimeTypes || type.granularity === "DAY") &&
      (editingGranularity == null ||
        type.granularity === editingGranularity ||
        (canLeaveAbsence && type.granularity === "TIME")),
  );
  const timeTypes = sortPickableTimeOffTypes(
    offered.filter((type) => type.granularity === "TIME"),
  );
  const dayTypes = offered.filter((type) => type.granularity === "DAY");

  // Types arrive after the form is built.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the lists arriving; re-running on every edit would undo a choice.
  useEffect(() => {
    if (editing != null || values.typeId != null) return;
    const preferred =
      initial.granularity === "DAY" && dayTypes.length > 0
        ? dayTypes[0]
        : (timeTypes[0] ?? dayTypes[0]);
    if (preferred) selectType(preferred);
  }, [timeTypes.length, dayTypes.length]);

  // Filled once per opened occurrence; a refetch would undo what was typed since.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the detail that was opened.
  useEffect(() => {
    if (editing == null || detail == null) return;
    const key = `${editing.target}:${editing.id}`;
    if (filledFrom === key) return;

    const next = valuesFromDetail(detail);
    form.setValues(next);
    setBaseline({
      recurrence: toRecurrence(next),
      typeId: next.typeId,
      allowsOnlineBooking: next.allowsOnlineBooking,
      compensation: JSON.stringify(compensationField(next, canSeeStaffCost)),
    });
    setFilledFrom(key);
  }, [detail, editing?.id, editing?.target]);

  function selectType(type: TimeOffTypeRow) {
    const current = form.getValues();
    const wasDay = granularity === "DAY";
    const isNewType = type.id !== current.typeId;
    form.setFieldValue("typeId", type.id);
    setSaveError(null);

    if (type.granularity === "DAY") {
      // Resource time off is always hour-level, so a day-level type drops them.
      form.setFieldValue("resourceIds", []);
      if (!wasDay) form.setFieldValue("endDate", current.date);
      return;
    }

    if (editing == null) {
      form.setFieldValue(
        "endTime",
        addMinutes(current.startTime, type.defaultDurationMins ?? 30),
      );
    }
    if (editing == null || isNewType) {
      form.setFieldValue("allowsOnlineBooking", !type.blocksOnlineBooking);
    }
    if (current.resourceIds.length > 0) {
      form.setFieldValue("title", resolveTimeOffTypeName(type));
    }
  }

  function setResourceIds(resourceIds: number[]) {
    form.setFieldValue("resourceIds", resourceIds);
    if (resourceIds.length > 0 && values.title === "" && selectedType) {
      form.setFieldValue("title", resolveTimeOffTypeName(selectedType));
    }
  }

  function setRepeat(repeat: RepeatOption) {
    // On an edit a new rule starts on the opened occurrence, whatever the date field says.
    const startsOn = occurrenceDate ?? values.date;
    form.setFieldValue("repeat", repeat);
    form.setFieldValue("weekdayMask", initialWeekdayMask(repeat, startsOn));
    if (repeat === "DATES" && values.pickedDates.length === 0) {
      form.setFieldValue("pickedDates", [startsOn]);
    }
  }

  const fieldErrors = findFieldErrors(values, granularity);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  // Save shows every field error; one that is wrong as typed shows at once.
  function errorFor(field: keyof TimeOffFormValues): string | undefined {
    const shown = form.errors[field];
    if (typeof shown === "string") return shown;
    const live = fieldErrors[field];
    return live === "INVALID_TIME" ||
      live === "INVALID_DATE" ||
      live === "TOO_MANY_TARGETS"
      ? fieldErrorMessage(live)
      : undefined;
  }

  const ruleChanged =
    editing != null &&
    !isDay &&
    filledFrom != null &&
    !sameRecurrence(toRecurrence(values), baseline.recurrence);

  const occurrenceDate =
    editing != null && !isDay ? (detail?.date ?? null) : null;
  const shown = pinRuleChangeDate(values, occurrenceDate, ruleChanged);

  // The server keeps an absence's started days, so an edit is judged from today on.
  const todayAtLocation =
    editing != null && detail != null
      ? dayjs().tz(detail.timeZone).format("YYYY-MM-DD")
      : null;
  const firstEditableDate =
    todayAtLocation != null && todayAtLocation > values.date
      ? todayAtLocation
      : values.date;

  // --- Preview -------------------------------------------------------------

  // Tagged with its shape, so a body debounced before a type switch never reaches the other endpoint.
  const previewKey = ((): string | null => {
    if (hasFieldErrors || locationId == null) return null;
    if (isDay) {
      return JSON.stringify({
        kind: "DAY",
        body: { ...toAbsenceBody(values), locationId },
      });
    }
    if (editing != null && detail == null) return null;

    const body = toTimeOffBody(shown, locationId);
    if (editing == null || body.recurrence == null) {
      return JSON.stringify({ kind: "TIME", body });
    }
    // An edit is previewed as the fresh series it would start on this day.
    if (!("dates" in body.recurrence)) {
      return JSON.stringify({ kind: "TIME", body });
    }
    const dates = body.recurrence.dates.filter((date) => date >= shown.date);
    if (dates.length === 0) return null;
    return JSON.stringify({
      kind: "TIME",
      body: { ...body, startsOn: dates[0], recurrence: { dates } },
    });
  })();
  const [debouncedKey] = useDebouncedValue(previewKey, 300);
  const isPreviewStale = previewKey !== debouncedKey;
  const debounced = debouncedKey
    ? (JSON.parse(debouncedKey) as { kind: TimeOffGranularity; body: unknown })
    : null;
  function bodyFor(kind: TimeOffGranularity) {
    return debounced?.kind === kind ? JSON.stringify(debounced.body) : null;
  }

  const timeQuery = api.timeoff.usePreviewTimeOff(bodyFor("TIME"));
  const createAbsenceQuery = api.timeoff.usePreviewTimeOffOverShifts(
    editing == null ? bodyFor("DAY") : null,
  );
  const resaveQuery = api.timeoff.usePreviewTimeOffOverShiftsResave(
    editing?.id ?? null,
    editing != null ? bodyFor("DAY") : null,
  );
  const absenceQuery = editing == null ? createAbsenceQuery : resaveQuery;
  const activeQuery = isDay ? absenceQuery : timeQuery;
  // The last preview stays up, dimmed, while the next loads, so summaries do not flicker.
  const timePreview =
    !isDay && previewKey != null && isTimeOffPreview(timeQuery.data)
      ? timeQuery.data
      : undefined;
  const absencePreview =
    isDay && previewKey != null ? absenceQuery.data : undefined;

  const previewError =
    activeQuery.error != null && !isPreviewStale
      ? timeOffErrorMessage(
          getApiErrorCode(activeQuery.error),
          activeQuery.error.response?.status,
        )
      : null;

  // --- Daily limit ---------------------------------------------------------

  // An edit has no dry run of its own, so the limit is judged here on the form's day.
  const { data: formDayData } = api.timeoff.useGetTimeoff(
    {
      locationId: locationId ?? 0,
      dateFrom: shown.date,
      dateTo: shown.date,
      includeResources: true,
    },
    editing != null && !isDay && locationId != null,
  );
  const editDailyLimit =
    editing != null && !isDay && selectedType != null
      ? findDailyLimitBreach({
          type: selectedType,
          rows: formDayData?.timeoffs ?? [],
          userIds: values.userIds,
          date: shown.date,
          durationMins: durationBetween(values.startTime, values.endTime),
          editingId: editing.target === "staff" ? editing.id : null,
        })
      : null;
  const timeDailyLimit =
    editing == null ? (timePreview?.dailyLimit ?? null) : null;
  const previewDailyLimit: DailyLimit | null = isDay
    ? (absencePreview?.dailyLimit ?? null)
    : timeDailyLimit;
  const dailyLimit: DailyLimit | null = editDailyLimit
    ? {
        userId: editDailyLimit.userId,
        totalMinutes: editDailyLimit.totalMinutes,
        maxDailyMinutes: selectedType?.maxDailyMinutes ?? 0,
        date: shown.date,
        typeId: selectedType?.id ?? 0,
      }
    : previewDailyLimit;

  // --- Writes --------------------------------------------------------------

  const { mutateAsync: create, isPending: isCreating } =
    api.timeoff.useCreateTimeOff({ displayNotification: false });
  const { mutateAsync: createAbsence, isPending: isCreatingAbsence } =
    api.timeoff.useCreateTimeOffOverShifts({ displayNotification: false });
  const { mutateAsync: resave, isPending: isResaving } =
    api.timeoff.useResaveTimeOffOverShifts({ displayNotification: false });
  const { mutateAsync: update, isPending: isUpdating } =
    api.timeoff.useUpdateTimeOffOccurrence({ displayNotification: false });
  const { mutateAsync: remove, isPending: isDeleting } =
    api.timeoff.useDeleteTimeOffOccurrence({ displayNotification: false });

  const isSaving =
    isCreating || isCreatingAbsence || isResaving || isUpdating || isDeleting;

  async function finish(message: string) {
    addToast({ title: message, color: "success" });
    await refreshAfterTimeOffWrite(queryClient, editing);
  }

  function nameOf(userId: number) {
    const person = people.find((candidate) => candidate.userId === userId);
    return person
      ? `${person.name ?? ""} ${person.lastName ?? ""}`.trim()
      : String(userId);
  }

  function reportSkipped(result: {
    userIdsWithoutShifts: number[];
    userIdsAlreadyAbsent: number[];
  }) {
    // Anyone skipped has to be named, or their absence reads as saved.
    const skipped = [
      ...result.userIdsWithoutShifts,
      ...result.userIdsAlreadyAbsent,
    ];
    if (skipped.length === 0) return;
    addToast({
      title: t`Nekaterim ni bilo dodano`,
      description: skipped.map(nameOf).join(", "),
      color: "warning",
    });
  }

  /** False when the save did not happen, so the caller keeps the form open. */
  async function run(write: () => Promise<void>): Promise<boolean> {
    setSaveError(null);
    try {
      await write();
      setOverlapConflict(null);
      return true;
    } catch (error) {
      const code = getApiErrorCode(error) ?? null;
      if (isAxiosError(error) && code === "TIME_OFF_OVERLAPS") {
        setOverlapConflict(error.response?.data as TimeOffOverlapConflictBody);
        return false;
      }
      setOverlapConflict(null);
      sentryLog.error("Saving a time off failed", describeErrorForLog(error));
      setSaveError({
        code,
        status: isAxiosError(error) ? (error.response?.status ?? null) : null,
      });
      return false;
    }
  }

  async function saveAbsence(onOverlap?: "SKIP" | "REPLACE") {
    const body = {
      ...toAbsenceBody(values),
      ...compensationField(values, canSeeStaffCost),
      onOverlap,
    };
    if (editing != null) {
      const result = await resave({ id: editing.id, ...body });
      reportSkipped(result);
      await finish(t`Odsotnost posodobljena`);
      return;
    }
    const result = await createAbsence({
      ...body,
      locationId: locationId as number,
    });
    reportSkipped(result);
    await finish(t`Odsotnost dodana`);
  }

  function editChanges(scope: TimeOffScope) {
    const isStaff = editing?.target === "staff";
    // Resources are typeless; a tile there only relabels the title.
    const typeChanged =
      isStaff && values.typeId != null && values.typeId !== baseline.typeId;
    const blocksOnlineBooking = !values.allowsOnlineBooking;
    // Omitted when unchanged, so the server copies a new type's own flag.
    const defaultBlocks = typeChanged
      ? selectedType?.blocksOnlineBooking
      : !baseline.allowsOnlineBooking;
    const compensation = compensationField(values, canSeeStaffCost);
    return {
      ...(JSON.stringify(compensation) !== baseline.compensation
        ? compensation
        : {}),
      startMinuteOfDay: minuteOfDay(values.startTime),
      durationMins: durationBetween(values.startTime, values.endTime),
      note: values.note.trim() || null,
      ...(typeChanged && values.typeId != null
        ? { typeId: values.typeId }
        : {}),
      // Refused for a series rewrite, so it only travels with ONLY_THIS.
      ...(scope === "ONLY_THIS" && !ruleChanged ? { date: values.date } : {}),
      ...(isStaff
        ? blocksOnlineBooking !== defaultBlocks
          ? { blocksOnlineBooking }
          : {}
        : { title: values.title.trim() || null }),
    };
  }

  async function submitEdit(scope: TimeOffScope) {
    if (editing == null) return;
    await update({
      target: editing.target,
      id: editing.id,
      scope,
      ...editChanges(scope),
      ...(ruleChanged ? { recurrence: toRecurrence(values) } : {}),
    });
    await finish(t`Odsotnost posodobljena`);
  }

  // A rule change always starts a fresh series from here, so it skips the scope prompt.
  async function save(): Promise<boolean> {
    form.setErrors(
      Object.fromEntries(
        Object.entries(fieldErrors).flatMap(([field, error]) =>
          error ? [[field, fieldErrorMessage(error)]] : [],
        ),
      ),
    );
    if (hasFieldErrors || dailyLimit != null || locationId == null) {
      return false;
    }

    if (isDay) return run(() => saveAbsence());

    if (editing == null) {
      return run(async () => {
        await create({
          ...toTimeOffBody(values, locationId),
          ...compensationField(values, canSeeStaffCost),
        });
        await finish(t`Odsotnost dodana`);
      });
    }

    if (ruleChanged) return run(() => submitEdit("THIS_AND_FOLLOWING"));
    if (detail?.seriesId != null) {
      setPendingScope({ intent: "save" });
      return false;
    }
    return run(() => submitEdit("ONLY_THIS"));
  }

  async function chooseOverlap(onOverlap: "SKIP" | "REPLACE") {
    return run(() => saveAbsence(onOverlap));
  }

  async function deleteEditing(): Promise<boolean> {
    if (editing == null) return false;
    if (detail?.seriesId != null) {
      setPendingScope({ intent: "delete" });
      return false;
    }
    return run(async () => {
      await remove({
        target: editing.target,
        id: editing.id,
        scope: "ONLY_THIS",
      });
      await finish(t`Odsotnost izbrisana`);
    });
  }

  async function confirmScope(scope: TimeOffScope): Promise<boolean> {
    const pending = pendingScope;
    setPendingScope(null);
    if (pending == null || editing == null) return false;

    if (pending.intent === "save") return run(() => submitEdit(scope));
    return run(async () => {
      await remove({ target: editing.target, id: editing.id, scope });
      await finish(t`Odsotnost izbrisana`);
    });
  }

  function dismissOverlap() {
    setOverlapConflict(null);
  }

  function dismissScope() {
    setPendingScope(null);
  }

  return {
    form,
    values: shown,
    people,
    allowDayTypes: allowDayTypes && editingGranularity !== "TIME",
    allowTimeTypes:
      allowTimeTypes && (editingGranularity !== "DAY" || canLeaveAbsence),
    granularity,
    isDay,
    timeTypes,
    dayTypes,
    selectedType,
    selectType,
    setResourceIds,
    setRepeat,
    fieldErrors,
    errorFor,
    editing,
    detail: detail ?? null,
    isLoadingDetail: editing != null && isLoadingDetail,
    ruleChanged,
    todayAtLocation,
    firstEditableDate,
    batchTargetCount: detail
      ? detail.batchTargets.userIds.length +
        detail.batchTargets.resourceIds.length
      : 0,
    timePreview: timePreview ?? null,
    absencePreview: absencePreview ?? null,
    isPreviewing: isPreviewStale || activeQuery.isFetching,
    previewError,
    dailyLimit,
    saveError: saveError
      ? timeOffErrorMessage(saveError.code, saveError.status)
      : null,
    overlapConflict,
    dismissOverlap,
    chooseOverlap,
    pendingScope,
    dismissScope,
    confirmScope,
    isSaving,
    // Field errors do not disable Save: pressing it is what shows them.
    canSave: dailyLimit == null && !isSaving,
    save,
    deleteEditing,
    nameOf,
    isCustomType: selectedType != null && isCustomTimeOffType(selectedType),
    canSeeStaffCost,
    compensationPresets: presetPercents(
      types.map((type) => type.compensation?.current?.percent),
    ),
    /** The type rate the time off falls back to: on its own day when opened, today's when new. */
    typeCompensation:
      editing != null && values.typeId === baseline.typeId
        ? (detail?.compensation?.typeRate ?? null)
        : (selectedType?.compensation?.current ?? null),
  };
}

export type DailyLimit = {
  date: string;
  userId: number;
  typeId: number;
  maxDailyMinutes: number;
  totalMinutes: number;
};

export type TimeOffFormState = ReturnType<typeof useTimeOffForm>;
